import { useEffect, useRef, useState } from "react";
import { Calendar, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  emptyCreatedDateFilter,
  formatCreatedDateDisplay,
  hasCreatedDateFilter,
  todayIsoDate,
  type CreatedDateFilterState,
} from "@sd/lib/ticketCreatedDateFilter";

interface CreatedDateFilterSelectProps {
  value: CreatedDateFilterState;
  onChange: (next: CreatedDateFilterState) => void;
  agentResponsive?: boolean;
  /** Full-width stacked fields on small screens (report filters). */
  responsive?: boolean;
  /** Cap selectable dates (`YYYY-MM-DD`). Defaults to today — future dates are never valid for a created-date filter. Pass `""` to allow any date. */
  max?: string;
}

function isNativeDatePickerOpen(input: HTMLInputElement): boolean {
  try {
    return input.matches(":open");
  } catch {
    return false;
  }
}

function DatePickerField({
  value,
  onChange,
  placeholder,
  ariaLabel,
  className,
  max,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  ariaLabel: string;
  className?: string;
  max?: string;
}) {
  const dateInputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef(value);
  const dismissedByPageClickRef = useRef(false);
  const [draft, setDraft] = useState(value);
  const displayValue = value ? formatCreatedDateDisplay(value) : "";

  valueRef.current = value;

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const input = dateInputRef.current;
      if (!input || !isNativeDatePickerOpen(input)) return;
      const root = rootRef.current;
      const target = event.target;
      if (target instanceof Node && root?.contains(target)) return;
      dismissedByPageClickRef.current = true;
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, []);

  const revertDraft = () => {
    setDraft(valueRef.current);
  };

  const commitIfDateClicked = (next: string, input: HTMLInputElement) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (dismissedByPageClickRef.current) {
          dismissedByPageClickRef.current = false;
          revertDraft();
          return;
        }
        if (isNativeDatePickerOpen(input)) return;
        if (next !== valueRef.current) onChange(next);
      });
    });
  };

  const openPicker = () => {
    const input = dateInputRef.current;
    if (!input || input.disabled) return;
    dismissedByPageClickRef.current = false;
    // Blank karo taaki browser dusre months me usi din-number ko blue na kare.
    // Real value valueRef me hai; onBlur / cancel pe wapas aa jayegi.
    setDraft("");
    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }
    input.focus();
    input.click();
  };

  return (
    <div ref={rootRef} className={cn("relative shrink-0", className)}>
      <button
        type="button"
        onClick={openPicker}
        className={cn(
          "border-input bg-background ring-offset-background focus-visible:ring-ring flex h-9 w-full items-center rounded-md border px-3 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
          displayValue ? "text-foreground pr-9" : "text-muted-foreground pr-9"
        )}
        aria-label={ariaLabel}
      >
        <span className="min-w-0 flex-1 truncate">
          {displayValue || placeholder}
        </span>
      </button>
      <Calendar className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground" />
      <input
        ref={dateInputRef}
        type="date"
        value={draft}
        max={max}
        onChange={e => {
          const next = e.target.value;
          setDraft(next);
          commitIfDateClicked(next, e.currentTarget);
        }}
        onBlur={() => {
          dismissedByPageClickRef.current = false;
          revertDraft();
        }}
        onCancel={() => {
          dismissedByPageClickRef.current = false;
          revertDraft();
        }}
        tabIndex={-1}
        aria-hidden
        className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
      />
    </div>
  );
}

/**
 * Created-date range filter — From / To (DD-MM-YYYY + native calendar).
 */
export function CreatedDateFilterSelect({
  value,
  onChange,
  agentResponsive = false,
  responsive = false,
  max = todayIsoDate(),
}: CreatedDateFilterSelectProps) {
  const showClear = hasCreatedDateFilter(value);

  const fieldWidth = responsive
    ? "w-full min-w-0 flex-1 sm:w-[124px]"
    : agentResponsive
      ? "w-[118px] lg:w-[124px]"
      : "w-[124px]";

  return (
    <div
      className={cn(
        "flex items-center gap-1.5",
        responsive ? "w-full flex-wrap sm:flex-nowrap" : "shrink-0"
      )}
    >
      <DatePickerField
        value={value.from}
        onChange={from => onChange({ ...value, from })}
        placeholder="From"
        ariaLabel="Created from date"
        className={fieldWidth}
        max={max}
      />
      <span className="shrink-0 text-xs text-muted-foreground">–</span>
      <DatePickerField
        value={value.to}
        onChange={to => onChange({ ...value, to })}
        placeholder="To"
        ariaLabel="Created to date"
        className={fieldWidth}
        max={max}
      />
      {showClear ? (
        <button
          type="button"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-input text-muted-foreground hover:text-foreground"
          onClick={() => onChange(emptyCreatedDateFilter())}
          aria-label="Clear created date range"
          title="Clear dates"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}
